import React, {useCallback, useMemo, useState} from 'react';
import {Block, Button, Card, Dialog, DialogBody, DialogFooter, Spinner, VF, ModalComponentProps, makeValidate, toaster, Icon} from '@mc/ui-v4';
import {Form, FormRenderProps} from 'react-final-form';
import {AccessibilityField} from './AccessibilityField';

import {OnChange} from 'react-final-form-listeners';
import {formSchema} from '../../validation/getFieldSchema';

import {DocumentApi, DocumentVO, EnumInterpretace, EnumTypLayerAttr, LayerAttributeVO, LayerCategoryVO} from '../../api';

import {allowSelectCiselnikList, buildCiselnikSelectTree, flattenCiselnikTree, isCiselnikTree, nestedSort, renderCiselnikTreeItem} from '../../helpers/DataMutators';
import {ImageDropzone} from '../image/ImageDropzone';
import {downloadDocument, downloadTemporaryFilePreview} from '../../api/urls';
import {useAppConfig} from '@mc/react-app-base';
import {DocumentDropzone} from './document/DocumentDropzone';
import {useDataLog} from '../../hooks/useDataLog';
import {DataLogTable} from '../tables/DataLogTable';
import {FormApi} from 'final-form';
import {LayerId} from '@mc/react-mapio';
import {mcGlobe} from '@mc/react-ui-icons';
import {sortCiselniky} from '../../helpers/Formatter';

type LayerCategoryWithVO = LayerCategoryVO & {attributes: LayerAttributeVO[]};

type Props = ModalComponentProps & {
    title: string;
    featureId: number;
    layerId: LayerId;
    btnTitle: string;
    initialValues?: LayerAttributeVO & {tmpImages?: number[]; tmpDocs?: DocumentVO[]; actionLabel?: string};
    attributes: LayerAttributeVO[];
    isEdit?: boolean;
    onSubmit: (data: LayerAttributeVO) => void;
};

/**
 * Modální dialog "Upravit nebo přidat atribut"
 * @param title * @param initialValues * @param attributes * @param onSubmit
 * @param btnTitle
 * @param attributes
 * @param featureId
 * @param isEdit
 * @param initialValues
 * @param onClose
 * @param onSubmit
 * @constructor
 */
export const AttributesModal: React.FC<Props> = ({title, btnTitle, attributes, featureId, layerId, initialValues, isEdit, onClose, onSubmit}) => {
    const useDataLogQuery = useDataLog(initialValues.layerId, featureId.toString(), isEdit);
    const [fileObjects, setFileObjects] = useState<DocumentVO[]>(initialValues.tmpDocs && initialValues.tmpDocs.length > 0 ? initialValues.tmpDocs : []);

    const validate = useMemo(() => makeValidate(formSchema(attributes)), []);
    const handleValidate = useCallback(async (data: LayerAttributeVO) => await validate(data), [validate]);
    let appConfig = useAppConfig();

    const editableAttributes = useMemo(() => attributes.filter(a => !a.nazev.startsWith('ATTR_')), [attributes]);

    const handleChangeFileObjects = (vals, change) => {
        setFileObjects(vals);
        change('tmpDocs', vals);
    };

    /**
     * Počáteční hodnoty formuláře připravíme pouze jednou při otevření dialogu (MCW-29129).
     *
     * Nadřazená komponenta (MapPage) vytváří objekt initialValues znovu při každém překreslení
     * a ModalManager z ui-v4 překreslí všechny otevřené dialogy vždy, když se objeví další dialog
     * nebo toast (např. potvrzení smazání fotografie / dokumentu). Pokud by se počáteční hodnoty
     * skládaly znovu, react-final-form by novou instanci vyhodnotil jako změnu initialValues
     * (nestabilní jsou zejména předvyplněné aktuální datum a normalizovaná pole číselníků)
     * a formulář by reinicializoval - uživatel by přišel o všechna vyplněná data i o nahrané soubory.
     */
    const {formInitialValues, isCategory} = useMemo(() => {
        // Props objekt nemutujeme, pracujeme s vlastní kopií.
        const values = {...initialValues} as typeof initialValues;
        let hasCategory: boolean = false;
        // Nastavení defaultní hodnoty atributů s boolean na False
        editableAttributes.forEach((att: LayerAttributeVO) => {
            hasCategory = att.kategorie !== undefined;
            if (att.typ === EnumTypLayerAttr.BOOLEAN) {
                if (values[att.nazev] === undefined) {
                    values[att.nazev] = false;
                }
            } else if (att.typ === EnumTypLayerAttr.ENUM) {
                // Hodnota číselníku přichází vždy jako pole (LayerAttributeEnumVO[]). Ve formuláři pracujeme s ID:
                // u vícehodnotového číselníku s polem ID, u jednohodnotového s jedním ID.
                const raw = values[att.nazev];
                if (raw !== undefined && raw !== null) {
                    const arr = Array.isArray(raw) ? raw : [raw];
                    const ids = arr.map(v => (v && v.id !== undefined && v.id !== null ? v.id : v)).filter(v => v !== undefined && v !== null);
                    values[att.nazev] = att.interpretace === EnumInterpretace.MULTI_CISELNIK ? ids : ids.length ? ids[0] : undefined;
                }
            } else if (att.defaultValue !== null && values[att.nazev] === undefined) {
                values[att.nazev] = att.defaultValue;
            }
            // Předvyplnění aktuálním datem při insertu pro atributy s příznakem vyplnitAktualniDatum
            if (att.typ === EnumTypLayerAttr.DATE_TIME && att.vyplnitAktualniDatum && !isEdit) {
                if (values[att.nazev] === undefined || values[att.nazev] === null) {
                    const formatted = new Date().toLocaleString('sv-SE').replace(' ', 'T');
                    values[att.nazev] = formatted;
                }
            }

            if (att.nazev.includes('.') && !/^\[".*"\]$/.test(att.nazev)) {
                att.nazev = `["${att.nazev}"]`;
            }
        });

        return {formInitialValues: values, isCategory: hasCategory};
    }, []);

    const categoriesAttrs: LayerCategoryWithVO[] = [];

    // Některé vrstvy nemají kategorie
    if (isCategory) {
        // Podle kategorií jednotlivých atributů vytvoříme pole jedinečných kategorií
        editableAttributes.forEach((att: LayerAttributeVO): void => {
            const existingCategory: LayerCategoryWithVO = categoriesAttrs.find(
                (category: LayerCategoryWithVO): boolean => category.nazev === att.kategorie?.nazev,
            );
            if (!existingCategory) {
                categoriesAttrs.push({...att.kategorie, attributes: []});
            }
        });
        // Seřadíme kategorie podle pořadí
        categoriesAttrs.sort(nestedSort('poradi'));
        // Projdeme atributy a přidáme do jednotlivých kategorií
        editableAttributes.forEach((att: LayerAttributeVO): void => {
            const existingCategory: LayerCategoryWithVO = categoriesAttrs.find(
                (category: LayerCategoryWithVO): boolean => category.nazev === att.kategorie?.nazev,
            );

            if (existingCategory.nazev === att.kategorie?.nazev) {
                existingCategory.attributes.push(att);
            }
        });
        // Projdeme kategorie a seřadíme atributy uvnitř podle pořadí
        categoriesAttrs.forEach((cat: LayerCategoryWithVO): void => {
            cat.attributes.sort(nestedSort('poradi'));
        });
    }

    const getAttributeLabel = (attribute: LayerAttributeVO): React.ReactNode => {
        const label = attribute.popis ? attribute.popis : attribute.nazev;
        if (attribute.publikovat) {
            return (
                <>
                    {label} <Icon icon={mcGlobe} />
                </>
            );
        }
        return label;
    };

    const renderAttributes = (attribute: LayerAttributeVO, submitting: boolean, _change, _form: FormApi<LayerAttributeVO, Partial<LayerAttributeVO>>) => {
        const label = getAttributeLabel(attribute);
        switch (attribute.typ) {
            case EnumTypLayerAttr.STRING:
                return (
                    <VF.String
                        key={`${attribute.nazev}-${attribute.nazev}-${attribute.id}`}
                        disabled={submitting}
                        name={`${attribute.nazev}`}
                        label={label}
                        required={attribute.povinnost}
                    />
                );
            case EnumTypLayerAttr.TEXT:
                // Dlouhý text se podle interpretace edituje buď jako formátovaný text (WYSIWYG), nebo jako prostý text.
                if (attribute.interpretace === EnumInterpretace.FORMATOVANYTEXT) {
                    return (
                        <VF.Wysiwyg
                            key={`${attribute.nazev}-${attribute.id}`}
                            disabled={submitting}
                            name={`${attribute.nazev}`}
                            label={label}
                            required={attribute.povinnost}
                        />
                    );
                }
                return (
                    <VF.TextArea
                        key={`${attribute.nazev}-${attribute.id}`}
                        disabled={submitting}
                        name={`${attribute.nazev}`}
                        label={label}
                        required={attribute.povinnost}
                    />
                );
            case EnumTypLayerAttr.INT:
                return (
                    <VF.Number
                        key={`${attribute.nazev}-${attribute.id}`}
                        className={`col-4 pl-0`}
                        disabled={submitting}
                        name={`${attribute.nazev}`}
                        label={label}
                        required={attribute.povinnost}
                    />
                );
            case EnumTypLayerAttr.NUMBER:
                return (
                    <VF.Number
                        key={`${attribute.nazev}-${attribute.id}`}
                        decimal
                        maximumFractionDigits={4}
                        className={`col-4 pl-0`}
                        disabled={submitting}
                        name={`${attribute.nazev}`}
                        label={label}
                        required={attribute.povinnost}
                    />
                );
            case EnumTypLayerAttr.BOOLEAN:
                return (
                    <VF.Checkbox
                        key={`${attribute.nazev}-${attribute.id}`}
                        disabled={submitting}
                        name={`${attribute.nazev}`}
                        label={label as string}
                        type="checkbox"
                        required={attribute.povinnost}
                    />
                );
            case EnumTypLayerAttr.ENUM: {
                // Vícehodnotový číselník (MULTI_CISELNIK) umožňuje výběr více hodnot (VF.MultiSelect).
                if (attribute.interpretace === EnumInterpretace.MULTI_CISELNIK) {
                    // VF.MultiSelect neumí stromový režim, u stromového číselníku proto hodnoty vypíšeme
                    // v pořadí stromu (rodič, pak jeho potomci) a hierarchii naznačíme odsazením.
                    const isTree = isCiselnikTree(attribute.ciselniky);
                    return (
                        <VF.MultiSelect
                            key={`${attribute.nazev}-${attribute.id}`}
                            name={`${attribute.nazev}`}
                            label={label}
                            items={
                                isTree
                                    ? flattenCiselnikTree(attribute.ciselniky).map(c => ({id: c.id, name: c.nazev, depth: c.depth}))
                                    : (sortCiselniky(attribute.ciselniky)?.map(c => ({id: c.id, name: c.nazev})) ?? [])
                            }
                            renderItem={isTree ? renderCiselnikTreeItem : undefined}
                            required={attribute.povinnost}
                        />
                    );
                }
                // Jednohodnotový číselník – stromový jako hierarchický výběr, plochý jako běžný výběr.
                // Ve stromu jde vybrat pouze koncová hodnota; uzly jsou vidět kvůli orientaci, ale
                // slouží jen k seskupení hodnot, nejsou to hodnoty atributu (MCW-29258).
                if (isCiselnikTree(attribute.ciselniky)) {
                    return (
                        <VF.SelectBox
                            key={`${attribute.nazev}-${attribute.id}`}
                            name={`${attribute.nazev}`}
                            label={label}
                            items={buildCiselnikSelectTree(attribute.ciselniky)}
                            tree
                            alwaysExpanded
                            useIdAsValue
                            allowSelectItem={allowSelectCiselnikList(attribute.ciselniky)}
                            required={attribute.povinnost}
                        />
                    );
                }
                return (
                    <VF.SelectBox
                        key={`${attribute.nazev}-${attribute.id}`}
                        name={`${attribute.nazev}`}
                        label={label}
                        items={sortCiselniky(attribute.ciselniky)}
                        useIdAsValue
                        required={attribute.povinnost}
                    />
                );
            }
            case EnumTypLayerAttr.DATE_TIME:
                return (
                    <VF.DateTime
                        key={`${attribute.nazev}-date-time`}
                        disabled={submitting}
                        name={`${attribute.nazev}`}
                        label={label}
                        required={attribute.povinnost}
                    />
                );
            case EnumTypLayerAttr.ACCESSIBILITY:
                return <AccessibilityField key={`${attribute.nazev}-${attribute.id}`} name={attribute.nazev} label={label} disabled={submitting} />;
            default:
                return console.warn('Invalid type');
        }
    };

    return (
        <Form<LayerAttributeVO> initialValues={formInitialValues} validate={handleValidate} onSubmit={onSubmit}>
            {({submitting, handleSubmit, values, form: {change}, form}: FormRenderProps<LayerAttributeVO>) => (
                <Dialog size="lg" onClose={onClose} title={title}>
                    <DialogBody>
                        {((isCategory && categoriesAttrs.length > 0) || editableAttributes.length > 0) && (
                            <Card mode={'edit'} title={initialValues?.actionLabel}>
                                <Block>
                                    {isCategory
                                        ? categoriesAttrs.map((cat: LayerCategoryWithVO) =>
                                              cat.attributes.map((attribute: LayerAttributeVO) => renderAttributes(attribute, submitting, change, form)),
                                          )
                                        : editableAttributes.map((attribute: LayerAttributeVO) => renderAttributes(attribute, submitting, change, form))}
                                </Block>
                            </Card>
                        )}
                        <Card mode={'edit'} title={'Fotografie'} className={'mt-2'}>
                            <Block bodyClassName={'pb-0'}>
                                <VF.Base
                                    name={`tmpImages`}
                                    input={ImageDropzone}
                                    label={false}
                                    getImageUrl={id => {
                                        return `${appConfig.serverContextPath}${
                                            typeof id == 'number' ? downloadDocument(String(featureId), id) : downloadTemporaryFilePreview(id)
                                        }`;
                                    }}
                                    onImageUpload={async (imageContent: File) => {
                                        const {data} = await DocumentApi.createTemporaryFile('H1', imageContent)
                                            .then(res => res)
                                            .catch(error => {
                                                if (error.status === 413) {
                                                    toaster.errorWithTitle('Nahrání fotografie se nezdařilo.', 'Soubor je pravděpodobně příliš velký.');
                                                } else {
                                                    toaster.error('Nahrání fotografie se nezdařilo.');
                                                }
                                                throw error;
                                            });
                                        return data;
                                    }}
                                    multiple
                                />
                            </Block>
                        </Card>
                        <Card mode={'edit'} title={'Dokumenty'} className={'mt-2'}>
                            <Block bodyClassName={'pb-0'}>
                                <VF.Base
                                    name={`tmpDocs`}
                                    input={DocumentDropzone}
                                    label={false}
                                    getDocumentUrl={id => {
                                        return `${appConfig.serverContextPath}${
                                            typeof id == 'number' ? downloadDocument(String(featureId), id) : downloadTemporaryFilePreview(id)
                                        }`;
                                    }}
                                    onDocumentUpload={async (imageContent: File) => {
                                        const {data} = await DocumentApi.createTemporaryFile('H1', imageContent)
                                            .then(res => res)
                                            .catch(error => {
                                                if (error.status === 413) {
                                                    toaster.errorWithTitle('Nahrání dokumentu se nezdařilo.', 'Soubor je pravděpodobně příliš velký.');
                                                } else {
                                                    toaster.error('Nahrání dokumentu se nezdařilo.');
                                                }
                                                throw error;
                                            });
                                        return data;
                                    }}
                                    fileObjects={fileObjects}
                                    setFileObjects={vals => handleChangeFileObjects(vals, change)}
                                    multiple
                                />
                            </Block>
                            <OnChange name={'tmpDocs'}>
                                {() => {
                                    change('tmpDocs', fileObjects);
                                }}
                            </OnChange>
                        </Card>
                        {isEdit && useDataLogQuery.data && useDataLogQuery.data.length > 0 && (
                            <Card mode={'edit'} title={'Historie'} className={'mt-2'}>
                                <Block>
                                    {useDataLogQuery.isLoading && <Spinner />}
                                    {useDataLogQuery.data && <DataLogTable datalog={useDataLogQuery.data} layerId={layerId} featureId={featureId.toString()} />}
                                </Block>
                            </Card>
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <Button disabled={submitting} onClick={onClose}>
                            Zrušit
                        </Button>
                        <Button style="primary" disabled={submitting} onClick={handleSubmit} type={'submit'}>
                            {btnTitle}
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}
        </Form>
    );
};
